import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import {
  useGetRouteLegsQuery,
  useUpdateLegDetailsMutation,
  useUpdateCarTrailerDetailsMutation,
  TaskName,
} from '../../../../generated/graphql';
import { useUserConfiguration } from '../../../../providers/UserConfigurationProvider';
import { TABLE_NAMES } from '../../../../lib/constants';
import { format, parseISO } from 'date-fns';

// ---------- constants ----------
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------- types ----------
interface DriverRow {
  legId?: number | undefined;
  locationName: string;
  routeDriverName: string;
  routeDriverPhoneNumber: string;
  routeDriverEmail: string;
  carRegistrationNumber: string;
  trailerRegistrationNumber: string;
  carDetailId?: number;
  trailerDetailId?: number;
  hasTrailerDetails: boolean;
}

export interface EditDriverDetailsModalProps {
  routeId: number;
  legId?: number;
  routeName: string;
  transportationDate: string;
  cellValue?: string;
  tableName?: string;
  onRowUpdate?: (updatedFields: {
    id: number;
    driverName: string;
    driverPhoneNumber: string;
    carRegistrationNumber: string;
    trailerRegistrationNumber: string;
  }) => void;
}

// ---------- helpers ----------

/** Auto-copy: propagate first-leg data to subsequent legs whose cells are empty */
function applyAutoCopy(rows: DriverRow[]): DriverRow[] {
  if (rows.length <= 1) return rows;
  const source = rows[0];
  return rows.map((row, idx) => {
    if (idx === 0) return row;
    return {
      ...row,
      routeDriverName: row.routeDriverName || source.routeDriverName,
      routeDriverPhoneNumber:
        row.routeDriverPhoneNumber || source.routeDriverPhoneNumber,
      routeDriverEmail: row.routeDriverEmail || source.routeDriverEmail,
      carRegistrationNumber:
        row.carRegistrationNumber || source.carRegistrationNumber,
      trailerRegistrationNumber:
        row.trailerRegistrationNumber || source.trailerRegistrationNumber,
    };
  });
}

/** Compare two row sets to find legs that were changed */
function getChangedRows(
  localRows: DriverRow[],
  originalRows: DriverRow[],
): DriverRow[] {
  return localRows.filter((row, idx) => {
    const orig = originalRows[idx];
    if (!orig) return true;
    return (
      row.routeDriverName !== orig.routeDriverName ||
      row.routeDriverPhoneNumber !== orig.routeDriverPhoneNumber ||
      row.routeDriverEmail !== orig.routeDriverEmail ||
      row.carRegistrationNumber !== orig.carRegistrationNumber ||
      row.trailerRegistrationNumber !== orig.trailerRegistrationNumber
    );
  });
}

// ---------- component ----------

const EditDriverDetailsModal: React.FC<EditDriverDetailsModalProps> = ({
  routeId,
  legId,
  routeName,
  transportationDate,
  cellValue,
  tableName,
  onRowUpdate,
}) => {
  const [open, setOpen] = useState(false);
  const [localRows, setLocalRows] = useState<DriverRow[]>([]);
  const [originalRows, setOriginalRows] = useState<DriverRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [emailErrors, setEmailErrors] = useState<Record<number, boolean>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const { globalDateFormat } = useUserConfiguration();

  const { data, loading } = useGetRouteLegsQuery({
    variables: { id: routeId },
    skip: !open,
    fetchPolicy: 'no-cache',
  });

  const [updateLegDetails] = useUpdateLegDetailsMutation();
  const [updateCarTrailerDetails] = useUpdateCarTrailerDetailsMutation();

  // --- populate local state when data arrives ---
  useEffect(() => {
    if (!data?.route?.legs || !open) return;

    const rows: DriverRow[] = data.route.legs
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((leg) => ({
        legId: leg.id,
        locationName:
          leg.location?.name ||
          leg.location?.city ||
          `Location ${leg.position + 1}`,
        routeDriverName: leg.routeDriverName || '',
        routeDriverPhoneNumber: leg.routeDriverPhoneNumber || '',
        routeDriverEmail: (leg as any).routeDriverEmail || '',
        carRegistrationNumber:
          leg.carDetails?.registrationNumber || leg.carRegistrationNumber || '',
        trailerRegistrationNumber:
          leg.trailerDetails?.registrationNumber ||
          leg.trailerRegistrationNumber ||
          '',
        carDetailId: leg.carDetails?.id ?? undefined,
        trailerDetailId: leg.trailerDetails?.id ?? undefined,
        hasTrailerDetails: !!leg.trailerDetails,
        postition: leg.position,
      }));

    const withAutoCopy = applyAutoCopy(rows);
    setLocalRows(withAutoCopy);
    // store originals (pre-auto-copy) so we only send API calls for truly changed rows
    setOriginalRows(rows);
  }, [data, open]);

  // --- field change handler ---
  const handleFieldChange = useCallback(
    (
      idx: number,
      field: keyof Omit<DriverRow, 'legId' | 'locationName'>,
      value: string,
    ) => {
      setLocalRows((prev) => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], [field]: value };
        return updated;
      });
      if (field === 'routeDriverEmail') {
        setEmailErrors((prev) => ({
          ...prev,
          [idx]: value.length > 0 && !EMAIL_REGEX.test(value),
        }));
      }
    },
    [],
  );

  // --- copy-down: copy row[idx] → row[idx+1] for empty cells only ---
  const handleCopyDown = useCallback((idx: number) => {
    setLocalRows((prev) => {
      if (idx + 1 >= prev.length) return prev;
      const source = prev[idx];
      const next = prev[idx + 1];
      const updated = [...prev];
      updated[idx + 1] = {
        ...next,
        routeDriverName: source.routeDriverName,
        routeDriverPhoneNumber: source.routeDriverPhoneNumber,
        routeDriverEmail: source.routeDriverEmail,
        carRegistrationNumber: source.carRegistrationNumber,
        trailerRegistrationNumber: source.trailerRegistrationNumber,
      };
      return updated;
    });
  }, []);

  const hasEmailError = Object.values(emailErrors).some(Boolean);

  // --- save ---
  const handleSave = async () => {
    if (hasEmailError) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const changedRows = getChangedRows(localRows, originalRows);
      await Promise.all(
        changedRows.flatMap((row) => {
          const orig = originalRows.find((o) => o.legId === row.legId);
          const promises = [];

          // Driver name / phone / email → updateLegDetails
          if (
            row.routeDriverName !== orig?.routeDriverName ||
            row.routeDriverPhoneNumber !== orig?.routeDriverPhoneNumber ||
            row.routeDriverEmail !== orig?.routeDriverEmail
          ) {
            promises.push(
              updateLegDetails({
                variables: {
                  input: {
                    id: row.legId ?? 0,
                    routeDriverName: row.routeDriverName || null,
                    routeDriverPhoneNumber: row.routeDriverPhoneNumber || null,
                    routeDriverEmail: (row.routeDriverEmail || null) as any,
                  },
                },
              }),
            );
          }

          // Car registration → updateCarTrailerDetails (Car)
          if (row.carRegistrationNumber !== orig?.carRegistrationNumber) {
            promises.push(
              updateCarTrailerDetails({
                variables: {
                  input: {
                    id: row.carDetailId ?? 0,
                    legId: row.legId,
                    type: TaskName.Car,
                    registrationNumber: row.carRegistrationNumber || null,
                  } as any,
                },
              }),
            );
          }

          // Trailer registration → updateCarTrailerDetails (Trailer)
          if (
            row.trailerRegistrationNumber !== orig?.trailerRegistrationNumber
          ) {
            promises.push(
              updateCarTrailerDetails({
                variables: {
                  input: {
                    id: row.trailerDetailId ?? 0,
                    legId: row.legId,
                    type: TaskName.Trailer,
                    registrationNumber: row.trailerRegistrationNumber || null,
                  } as any,
                },
              }),
            );
          }

          return promises;
        }),
      );
      const useLegId =
        tableName === TABLE_NAMES.TruckFillAndTime ||
        tableName === TABLE_NAMES.LegsList;
      const currentUpdatedRow = useLegId
        ? localRows.find((r) => r.legId === legId)
        : localRows[0];
      handleClose();
      onRowUpdate?.({
        id: useLegId ? legId ?? 0 : routeId,
        driverName: currentUpdatedRow?.routeDriverName ?? '',
        driverPhoneNumber: currentUpdatedRow?.routeDriverPhoneNumber ?? '',
        carRegistrationNumber: currentUpdatedRow?.carRegistrationNumber ?? '',
        trailerRegistrationNumber:
          currentUpdatedRow?.trailerRegistrationNumber ?? '',
      });
    } catch (err: any) {
      setSaveError(err?.message || 'Save failed. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLocalRows([]);
    setOriginalRows([]);
    setSaveError(null);
    setEmailErrors({});
  };

  // --- derive last-updated metadata ---
  const lastUpdatedInfo = (() => {
    if (!open || !data?.route?.legs) return null;
    const latest = data.route.legs.reduce<(typeof data.route.legs)[0] | null>(
      (prev, curr) => {
        if (!prev) return curr;
        const currAt = (curr as any).updatedAt || '';
        const prevAt = (prev as any).updatedAt || '';
        return currAt > prevAt ? curr : prev;
      },
      null,
    );
    if (!latest) return null;
    const updatedAt = (latest as any).updatedAt as string | undefined;
    const lastUpdatedBy = (latest as any).lastUpdatedBy as string | undefined;
    let dateStr = updatedAt || '';
    if (updatedAt) {
      try {
        const dateFormat = globalDateFormat?.split(' ')[0] || 'yyyy-MM-dd';
        dateStr = format(new Date(updatedAt), dateFormat);
      } catch {
        /* keep raw string */
      }
    }
    return { dateStr, lastUpdatedBy };
  })();

  // --- format transportationDate for the header ---
  const formattedTransportDate = (() => {
    try {
      const dateFormat = globalDateFormat?.split(' ')[0] || 'yyyy-MM-dd';
      return format(parseISO(transportationDate), dateFormat);
    } catch {
      return transportationDate;
    }
  })();

  return (
    <>
      {/* Trigger: the cell value acts as the click target */}
      <Box
        onClick={() => setOpen(true)}
        sx={{
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" noWrap>
          {cellValue ?? ''}
        </Typography>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: 320 } }}
      >
        {/* ---- Header ---- */}
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            py: 1.5,
            px: 3,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box>
            <Typography
              component="div"
              sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 0.25 }}
            >
              {routeName} | {formattedTransportDate} |{' '}
              <strong style={{ color: '#000', fontWeight: 700 }}>
                EDIT DRIVER DETAILS
              </strong>
            </Typography>
            {lastUpdatedInfo && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
              >
                LAST UPDATED: {lastUpdatedInfo.dateStr}
                {lastUpdatedInfo.lastUpdatedBy
                  ? ` | LAST UPDATED BY: ${lastUpdatedInfo.lastUpdatedBy.toUpperCase()}`
                  : ''}
              </Typography>
            )}
          </Box>

          {/* Action buttons top-right */}
          <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label="Close without saving"
              sx={{
                backgroundColor: '#9e9e9e',
                color: '#fff',
                borderRadius: 1,
                '&:hover': { backgroundColor: '#757575' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              onClick={handleSave}
              size="small"
              disabled={isSaving || hasEmailError}
              aria-label="Save driver details"
              sx={{
                backgroundColor: '#4caf50',
                color: '#fff',
                borderRadius: 1,
                '&:hover': { backgroundColor: '#388e3c' },
                '&.Mui-disabled': { backgroundColor: '#a5d6a7', color: '#fff' },
              }}
            >
              {isSaving ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <CheckIcon />
              )}
            </IconButton>
          </Box>
        </DialogTitle>

        {/* ---- Content ---- */}
        <DialogContent sx={{ px: 3, pt: 2, pb: 3 }}>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <Table
              size="small"
              sx={{ '& .MuiTableCell-root': { borderColor: '#e0e0e0' } }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#f5f5f5',
                    '& th': {
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      py: 1,
                      color: '#555',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableCell>LOCATION</TableCell>
                  <TableCell>DRIVER NAME</TableCell>
                  <TableCell>DRIVER PHONE</TableCell>
                  <TableCell>DRIVER EMAIL</TableCell>
                  <TableCell>CAR REG. NB</TableCell>
                  <TableCell>TRAILER REG. NB</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {localRows.map((row, idx) => (
                  <TableRow
                    key={row.legId}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                      '& td': { py: 0.75 },
                    }}
                  >
                    {/* Location (read-only) */}
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        minWidth: 100,
                      }}
                    >
                      {row.locationName.toUpperCase()}
                    </TableCell>

                    {/* Driver Name */}
                    <TableCell>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={row.routeDriverName}
                        onChange={(e) =>
                          handleFieldChange(
                            idx,
                            'routeDriverName',
                            e.target.value,
                          )
                        }
                        inputProps={{ maxLength: 100 }}
                        sx={{ minWidth: 140 }}
                      />
                    </TableCell>

                    {/* Driver Phone – no length restriction */}
                    <TableCell>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={row.routeDriverPhoneNumber}
                        onChange={(e) =>
                          handleFieldChange(
                            idx,
                            'routeDriverPhoneNumber',
                            e.target.value,
                          )
                        }
                        sx={{ minWidth: 140 }}
                      />
                    </TableCell>

                    {/* Driver Email – format validation */}
                    <TableCell>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={row.routeDriverEmail}
                        onChange={(e) =>
                          handleFieldChange(
                            idx,
                            'routeDriverEmail',
                            e.target.value,
                          )
                        }
                        error={emailErrors[idx] === true}
                        helperText={
                          emailErrors[idx] ? 'Invalid email format' : undefined
                        }
                        inputProps={{ maxLength: 254 }}
                        sx={{ minWidth: 180 }}
                      />
                    </TableCell>

                    {/* Car Registration */}
                    <TableCell>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={row.carRegistrationNumber}
                        onChange={(e) =>
                          handleFieldChange(
                            idx,
                            'carRegistrationNumber',
                            e.target.value.toUpperCase(),
                          )
                        }
                        inputProps={{ maxLength: 20 }}
                        sx={{ minWidth: 110 }}
                      />
                    </TableCell>

                    {/* Trailer Registration */}
                    <TableCell>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={row.trailerRegistrationNumber}
                        onChange={(e) =>
                          handleFieldChange(
                            idx,
                            'trailerRegistrationNumber',
                            e.target.value.toUpperCase(),
                          )
                        }
                        disabled={!row.hasTrailerDetails}
                        helperText={
                          !row.hasTrailerDetails
                            ? 'Trailer is not available'
                            : undefined
                        }
                        inputProps={{ maxLength: 20 }}
                        sx={{ minWidth: 110 }}
                      />
                    </TableCell>

                    {/* Copy Down action (all rows except the last) */}
                    <TableCell sx={{ minWidth: 110 }}>
                      {idx < localRows.length - 1 && (
                        <Button
                          size="small"
                          onClick={() => handleCopyDown(idx)}
                          startIcon={<KeyboardDoubleArrowDownIcon />}
                          sx={{
                            color: '#4caf50',
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            px: 0.5,
                          }}
                        >
                          Copy Down
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditDriverDetailsModal;
